import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { normalizeSiteLanguage } from '../../utils/currentSiteLanguage';
import './draggable-chat-launcher.css';

type Props = { visible: boolean; onMinimize: () => void };
type Position = { left: number; top: number };

const LABELS = {
  hk: '收起對話',
  cn: '收起对话',
  en: 'Minimize chat',
  jp: 'チャットを最小化',
  kr: '채팅 최소화',
  th: 'ย่อแชท',
  vn: 'Thu nhỏ chat',
  de: 'Chat minimieren',
  fr: 'Réduire le chat',
  it: 'Riduci chat',
  es: 'Minimizar chat',
  ug: 'پاراڭنى كىچىكلىتىش',
};

export default function ChatMinimizeButton({ visible, onMinimize }: Props) {
  const { i18n } = useTranslation();
  const language = normalizeSiteLanguage(i18n.resolvedLanguage || i18n.language);
  const label = LABELS[language];
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<Position | null>(null);

  useLayoutEffect(() => {
    if (!visible) return;

    let animationFrame = 0;
    let observedFrame: HTMLElement | null = null;
    let observedContainer: HTMLElement | null = null;
    const scheduleLayout = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(layout);
    };
    const resizeObserver = new ResizeObserver(scheduleLayout);
    const widgetObserver = new MutationObserver(scheduleLayout);

    function layout() {
      const button = buttonRef.current;
      // Read only the host elements' bounds. Never access or restyle the
      // cross-origin chat document, its conversation, or native controls.
      const frame = document.getElementById('chat-widget');
      const container = document.getElementById('chat-widget-container');
      if (frame !== observedFrame || container !== observedContainer) {
        resizeObserver.disconnect();
        widgetObserver.disconnect();
        if (button) resizeObserver.observe(button);
        if (frame) resizeObserver.observe(frame);
        if (container) {
          resizeObserver.observe(container);
          widgetObserver.observe(container, {
            attributes: true,
            attributeFilter: ['style', 'class', 'hidden'],
            childList: true,
            subtree: true,
          });
        }
        observedFrame = frame;
        observedContainer = container;
      }
      if (!button || !frame || !container) {
        setPosition(null);
        return;
      }
      const rect = frame.getBoundingClientRect();
      const containerStyle = getComputedStyle(container);
      if (rect.width < 200 || rect.height < 100 || containerStyle.visibility === 'hidden'
        || containerStyle.display === 'none' || container.hidden) {
        setPosition(null);
        return;
      }

      const viewport = window.visualViewport;
      const viewLeft = viewport?.offsetLeft || 0;
      const viewTop = viewport?.offsetTop || 0;
      const viewWidth = viewport?.width || window.innerWidth;
      const viewHeight = viewport?.height || window.innerHeight;
      const style = getComputedStyle(button);
      const safeInset = (side: string) => Number.parseFloat(style.getPropertyValue(`--chat-safe-${side}`)) || 0;
      const edgeToEdge = rect.width >= window.innerWidth - 2;
      const gutter = edgeToEdge ? 8 : 24;
      const minX = viewLeft + safeInset('left') + 8;
      const minY = viewTop + safeInset('top') + 8;
      const maxX = Math.max(minX, viewLeft + viewWidth - safeInset('right') - button.offsetWidth - 8);
      const maxY = Math.max(minY, viewTop + viewHeight - safeInset('bottom') - button.offsetHeight - 8);
      const next = {
        left: Math.round(Math.max(minX, Math.min(maxX, rect.right - gutter - button.offsetWidth))),
        top: Math.round(Math.max(minY, Math.min(maxY, rect.top + (edgeToEdge ? 4 : 16)))),
      };
      setPosition((previous) => previous?.left === next.left && previous.top === next.top ? previous : next);
    }

    // Also handle an iframe inserted or replaced just after the ready callback.
    const bodyObserver = new MutationObserver(scheduleLayout);
    bodyObserver.observe(document.body, { childList: true });
    layout();
    window.addEventListener('resize', scheduleLayout);
    window.visualViewport?.addEventListener('resize', scheduleLayout);
    window.visualViewport?.addEventListener('scroll', scheduleLayout);
    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      widgetObserver.disconnect();
      bodyObserver.disconnect();
      window.removeEventListener('resize', scheduleLayout);
      window.visualViewport?.removeEventListener('resize', scheduleLayout);
      window.visualViewport?.removeEventListener('scroll', scheduleLayout);
    };
  }, [visible, language]);

  if (!visible) return null;

  return createPortal(
    <button
      ref={buttonRef}
      type="button"
      className="jlbhk-chat-minimize"
      data-testid="chat-minimize"
      lang={language === 'hk' ? 'zh-Hant' : language === 'cn' ? 'zh-Hans' : language === 'jp' ? 'ja' : language === 'kr' ? 'ko' : language === 'vn' ? 'vi' : language}
      dir={language === 'ug' ? 'rtl' : 'ltr'}
      aria-label={label}
      title={label}
      style={{ left: position?.left ?? 0, top: position?.top ?? 0, visibility: position ? 'visible' : 'hidden' }}
      onClick={onMinimize}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span>{label}</span>
    </button>,
    document.body,
  );
}
